/**
 * Configuración → Tarjetas (`/configuracion/tarjetas`).
 *
 * Puerto de `Card_brands` + `views/card_brands/index.php`. La tabla sólo tiene
 * dos datos: el **código** (columna `id`, `char(2)`, lo escribía el usuario) y
 * la descripción de la marca.
 *
 * El panel anterior dejaba el código vacío en las filas nuevas: si el `INSERT`
 * fallaba (código repetido) el `UPDATE` posterior no afectaba a ninguna fila y
 * la pantalla no avisaba de nada. Aquí el código se valida y el duplicado
 * responde 409.
 */

import { ArrowLeft, CreditCard, Pencil, Plus, ShieldAlert, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert, ICONO_EN_ALERTA } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ConfirmDialog, Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { ApiError } from '@/lib/http/api-error';

import { useMutarTarjetas, useTarjetas } from '../catalogos.api';
import type { Tarjeta } from '../catalogos.types';

/** Mensaje legible de un error (de la API o de red). */
function mensajeDe(error: unknown, porDefecto: string): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;

  return porDefecto;
}

/** Alta/edición de una tarjeta. */
function TarjetaDialogo({
  tarjeta,
  guardando,
  error,
  onGuardar,
  onCerrar,
}: {
  tarjeta: Tarjeta | null;
  guardando: boolean;
  error: string | null;
  onGuardar: (codigo: string, descripcion: string) => void;
  onCerrar: () => void;
}) {
  const [codigo, setCodigo] = useState(tarjeta?.codigo ?? '');
  const [descripcion, setDescripcion] = useState(tarjeta?.descripcion ?? '');

  const codigoValido = /^[A-Z0-9]{1,2}$/.test(codigo.trim().toUpperCase());
  const valido = codigoValido && descripcion.trim().length > 0;

  return (
    <Dialog
      open
      onClose={onCerrar}
      title={tarjeta ? 'Editar tarjeta' : 'Nueva tarjeta'}
      description={
        tarjeta
          ? 'El código es la clave de la tabla: no se puede cambiar.'
          : 'El código es el que se guarda en las ventas con tarjeta (hasta 2 caracteres).'
      }
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onCerrar} disabled={guardando}>
            Cancelar
          </Button>
          <Button
            size="sm"
            loading={guardando}
            disabled={!valido}
            onClick={() => onGuardar(codigo.trim().toUpperCase(), descripcion.trim())}
          >
            Guardar
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? <Alert variant="error" title="No se pudo guardar">{error}</Alert> : null}

        <Input
          label="Código"
          value={codigo}
          maxLength={2}
          autoFocus={!tarjeta}
          disabled={Boolean(tarjeta)}
          hint={tarjeta ? undefined : '1 o 2 caracteres (ej. VI, MC)'}
          placeholder="VI"
          className="uppercase"
          onChange={(evento) => setCodigo(evento.target.value.toUpperCase())}
        />

        <Input
          label="Descripción"
          value={descripcion}
          maxLength={255}
          autoFocus={Boolean(tarjeta)}
          placeholder="Ej. VISA"
          onChange={(evento) => setDescripcion(evento.target.value)}
        />
      </div>
    </Dialog>
  );
}

export function TarjetasPage() {
  useDocumentTitle('Configuración · Tarjetas');

  const tarjetas = useTarjetas();
  const mutaciones = useMutarTarjetas();

  const [dialogo, setDialogo] = useState<{ abierto: boolean; tarjeta: Tarjeta | null }>({
    abierto: false,
    tarjeta: null,
  });
  const [porEliminar, setPorEliminar] = useState<Tarjeta | null>(null);
  const [aviso, setAviso] = useState<{ tono: 'success' | 'error'; texto: string } | null>(null);
  const [errorDialogo, setErrorDialogo] = useState<string | null>(null);

  const sinPermiso = tarjetas.error instanceof ApiError && tarjetas.error.status === 403;
  const lista = tarjetas.data?.tarjetas ?? [];

  const cabecera = (
    <header className="flex flex-wrap items-start justify-between gap-4">
      <div>
        <Link
          to="/configuracion"
          className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-ink-400 transition-colors hover:text-brand-600"
        >
          <ArrowLeft className="size-3" aria-hidden="true" />
          Configuración
        </Link>
        <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight text-ink-900">
          <CreditCard className="size-6 text-brand-500" aria-hidden="true" />
          Tarjetas
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-ink-500">
          Marcas de tarjeta aceptadas en las ventas. El código de 2 caracteres es el que se guarda
          en cada transacción.
        </p>
      </div>

      <Button
        size="sm"
        leftIcon={<Plus className="size-3.5" aria-hidden="true" />}
        disabled={sinPermiso}
        onClick={() => {
          setErrorDialogo(null);
          setDialogo({ abierto: true, tarjeta: null });
        }}
      >
        Nueva tarjeta
      </Button>
    </header>
  );

  if (sinPermiso) {
    return (
      <div className="space-y-6">
        {cabecera}
        <Alert variant="warning" title="Acceso restringido">
          <span className={ICONO_EN_ALERTA}>
            <ShieldAlert className="size-4" aria-hidden="true" />
            Esta pantalla es del grupo <strong>Configuración</strong>.
          </span>
        </Alert>
      </div>
    );
  }

  const guardar = (codigo: string, descripcion: string) => {
    setErrorDialogo(null);

    if (dialogo.tarjeta) {
      mutaciones.actualizar.mutate(
        { codigo: dialogo.tarjeta.codigo, descripcion },
        {
          onSuccess: (datos) => {
            setDialogo({ abierto: false, tarjeta: null });
            setAviso({ tono: 'success', texto: datos.mensaje });
          },
          onError: (error) => setErrorDialogo(mensajeDe(error, 'No se pudo guardar la tarjeta.')),
        },
      );
      return;
    }

    mutaciones.crear.mutate(
      { codigo, descripcion },
      {
        onSuccess: (datos) => {
          setDialogo({ abierto: false, tarjeta: null });
          setAviso({ tono: 'success', texto: datos.mensaje });
        },
        onError: (error) => setErrorDialogo(mensajeDe(error, 'No se pudo guardar la tarjeta.')),
      },
    );
  };

  const eliminar = () => {
    if (!porEliminar) return;

    mutaciones.eliminar.mutate(porEliminar.codigo, {
      onSuccess: (datos) => {
        setPorEliminar(null);
        setAviso({ tono: 'success', texto: datos.mensaje });
      },
      onError: (error) => {
        setPorEliminar(null);
        setAviso({ tono: 'error', texto: mensajeDe(error, 'No se pudo eliminar la tarjeta.') });
      },
    });
  };

  return (
    <div className="space-y-6">
      {cabecera}

      {aviso ? (
        <Alert variant={aviso.tono === 'success' ? 'success' : 'error'}>{aviso.texto}</Alert>
      ) : null}

      {tarjetas.isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : tarjetas.error && !sinPermiso ? (
        <Alert variant="error" title="No se pudieron leer las tarjetas">
          {mensajeDe(tarjetas.error, 'La API no respondió.')}
        </Alert>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Listado de tarjetas</CardTitle>
            <Badge variant="neutral">{tarjetas.data?.total ?? 0} registradas</Badge>
          </CardHeader>
          <CardContent>
            {lista.length === 0 ? (
              <p className="py-6 text-center text-sm text-ink-500">
                Todavía no hay tarjetas registradas. Añade las marcas que acepta la estación (por
                ejemplo VI para VISA y MC para Mastercard).
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[32rem] border-collapse text-sm">
                  <thead>
                    <tr className="text-left text-[11px] font-semibold uppercase tracking-wide text-ink-400">
                      <th className="w-24 pb-2">Código</th>
                      <th className="pb-2">Descripción</th>
                      <th className="w-28 pb-2">Estado</th>
                      <th className="w-24 pb-2 text-right">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lista.map((tarjeta) => (
                      <tr key={tarjeta.codigo} className="border-t border-ink-100">
                        <td className="py-3">
                          <span className="rounded-control bg-ink-100 px-2 py-0.5 font-mono text-xs font-semibold text-ink-700">
                            {tarjeta.codigo}
                          </span>
                        </td>
                        <td className="py-3 font-medium text-ink-900">{tarjeta.descripcion}</td>
                        <td className="py-3">
                          <Badge variant={tarjeta.activo ? 'success' : 'neutral'}>
                            {tarjeta.activo ? 'Activa' : 'Inactiva'}
                          </Badge>
                        </td>
                        <td className="py-3">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label={`Editar ${tarjeta.descripcion}`}
                              title="Editar"
                              onClick={() => {
                                setErrorDialogo(null);
                                setDialogo({ abierto: true, tarjeta });
                              }}
                            >
                              <Pencil className="size-4" aria-hidden="true" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label={`Eliminar ${tarjeta.descripcion}`}
                              title="Eliminar"
                              onClick={() => setPorEliminar(tarjeta)}
                            >
                              <Trash2 className="size-4 text-rose-500" aria-hidden="true" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {dialogo.abierto ? (
        <TarjetaDialogo
          key={`tarjeta-${dialogo.tarjeta?.codigo ?? 'nueva'}`}
          tarjeta={dialogo.tarjeta}
          guardando={mutaciones.crear.isPending || mutaciones.actualizar.isPending}
          error={errorDialogo}
          onGuardar={guardar}
          onCerrar={() => setDialogo({ abierto: false, tarjeta: null })}
        />
      ) : null}

      <ConfirmDialog
        open={porEliminar !== null}
        title="¿Eliminar la tarjeta?"
        description={`Se eliminará «${porEliminar?.codigo ?? ''} · ${porEliminar?.descripcion ?? ''}».`}
        confirmLabel="Sí, eliminar"
        loading={mutaciones.eliminar.isPending}
        onClose={() => setPorEliminar(null)}
        onConfirm={eliminar}
      />
    </div>
  );
}
